import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../../components/buttons/Button";
import Breadcrumbs from "../../../components/BreadCrumbs";
import StyledHeadingText from "../../../components/Fonts/StyledHeadingText";
import { toast } from "react-toastify";
import CustomLoading from "../../../components/customLoading/CustomLoading";
import Pagination from "../../../components/tables/pagination/Pagination";
import Table from "../../../components/tables/Table";
import { formatDateTime } from "../../../utils/FormatDateTime";
import PatientPhotoGalleryComponent from "../../../components/PatientPhotoGalleryComponent";
import { getNewHudPatientsPackageApprovals, postNewHudPatientsPackageApprovals } from "../../../services/new_clinic/new_hud_patients";

const NewPatientApproval = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [rowData, setRowData] = useState({});
    const headers = [
        { label: "Uploaded On", key: "created_at" },
        { label: "Image", key: "image" },
    ];

    const [tableData, setTableData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const pageSize = 10;

    const fetchData = async (patientId) => {
        setLoading(true);
        try {
            let res = await getNewHudPatientsPackageApprovals(patientId);
            if (res.status === 200) {
                setTableData(res.data);
            } else {
                toast.error("Unable to Photos");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching Photos:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    useEffect(() => {
        const patientData = location.state;
        if (patientData) {
            setRowData(patientData);
            fetchData(patientData.id);
        } else {
            toast.error("Error while looking for patient");
            navigate(-1);
        }
    }, [location.state]);

    const choosePhoto = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        // fileInput.multiple = false;

        fileInput.onchange = (e) => {
            const files = Array.from(e.target.files);
            if (files.length > 20) {
                toast.error('You can select up to 20 images at a time');
                return;
            }
            const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
            for (const file of files) {
                if (file.size > 10 * 1024 * 1024) {
                    toast.error('File size should be less than 10MB');
                    return;
                }
                if (!validTypes.includes(file.type)) {
                    toast.error('Please select a valid image file (JPEG, PNG, or GIF)');
                    return;
                }
            }
            files.forEach(file => handlePhotoSubmit(file));
        };

        fileInput.click();
    };

    const handlePhotoSubmit = async (file) => {
        const formData = new FormData();
        formData.append('approval_image', file);
        formData.append('patient', rowData.id);
        setLoading(true);
        try {
            const response = await postNewHudPatientsPackageApprovals(formData);

            if (response.status === 201) {
                fetchData(rowData.id);
                toast.success("Image uploaded successfully");
            } else {
                toast.error("Unable to upload image");
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to upload photo');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md" onContextMenu={e => e.preventDefault()}>
            <Breadcrumbs items={[
                { name: 'Home', link: '/package-invoice' },
                { name: 'Patients', link: '/new-hud-patients' },
                { name: 'Package', link: (-1) },
                { name: 'Patient Approvals' },
            ]} />
            <div className="my-4">
                <StyledHeadingText text={`${rowData.name} (${"Age: " + rowData.age || "DOB: " + rowData.dob} | Gen: ${rowData.gender})`} />
            </div>
            <div className="mt-2 flex flex-col">
                <div className="w-full">
                    <div className="flex flex-row justify-between items-center my-4">
                        <StyledHeadingText text={"Approvals"} />
                        <Button
                            type="button"
                            className="bg-primary text-white px-4 py-2 rounded"
                            onClick={choosePhoto}
                            disabled={loading}
                        >
                            {loading ? "Uploading..." : "Add Image"}
                        </Button>
                    </div>
                    {loading ?
                        <div className="flex w-full flex-row justify-center my-2">
                            <CustomLoading size={100} />
                        </div>
                        :
                        <Table
                            headers={headers}
                            rows={tableData?.results?.map((item, index) => (
                                {
                                    created_at: <p>{formatDateTime(item.created_at)}</p>,
                                    image: <div className="h-10 w-10 border border-gray-300 rounded-md"><PatientPhotoGalleryComponent isOperator={false} imageToDisplay={item.photo} images={tableData.results} activeIndex={index} className="aspect-square rounded-md max-w-10 border shadow-lg" /></div>,
                                }))}
                        />
                    }
                    {tableData.count !== 0 ?
                        <div className="xl:pl-5 pl-0 w-full flex-1">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(tableData?.count / pageSize)}
                                pageSize={pageSize}
                                totalEntries={tableData?.count}
                                onPageChange={handlePageChange}
                                data={tableData}
                                setTableData={setTableData}
                            />
                        </div> : <></>}
                </div>
            </div>

            <div className="flex gap-4 justify-end w-full mt-4">
                <Button
                    type="button"
                    className="bg-gray-500 text-white px-4 py-2 rounded"
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
            </div>
        </div >
    );
};

export default NewPatientApproval;
